import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import api from "../../utils/api";
import {
  Mail, Send, Search, RefreshCw, Loader2, X, CheckCircle, AlertCircle,
  Plus, Copy, Trash2, Eye, Monitor, Smartphone, Upload, ChevronRight,
  FlaskConical, Save, Clock, Variable, Image, ChevronDown, Palette,
} from "lucide-react";

// ─── Brand Configs ───────────────────────────────────────────────────────────
const BRANDS = {
  tinyglobal: {
    label: "TinyGlobal",
    primaryColor: "#071d2e",
    accentColor: "#c9a227",
    loginUrl: "https://app.tinyglobalop.com/login",
    footerText: "© 2026 TinyGlobal Pty Ltd · Sydney, Australia\nBuilt by Yadata · contact@yadata.com.au",
    headerBg: "#071d2e",
    logo: null,
  },
  yadata: {
    label: "Yadata",
    primaryColor: "#1a237e",
    accentColor: "#ffd600",
    loginUrl: "https://www.yadata.com.au",
    footerText: "© 2026 Yadata Pty Ltd · Sydney, Australia\nwww.yadata.com.au · contact@yadata.com.au",
    headerBg: "#1a237e",
    logo: null,
  },
};

// ─── Variables ───────────────────────────────────────────────────────────────
const ALL_VARS = [
  { key: "recipientName",  label: "Recipient Name" },
  { key: "companyName",    label: "Company Name" },
  { key: "role",           label: "Role" },
  { key: "shipmentNumber", label: "Shipment Number" },
  { key: "invoiceNumber",  label: "Invoice Number" },
  { key: "amountDue",      label: "Amount Due" },
  { key: "loginUrl",       label: "Login URL" },
  { key: "supportEmail",   label: "Support Email" },
  { key: "note",           label: "Note / Message" },
  { key: "status",         label: "Status" },
];

const DUMMY_VARS = {
  recipientName: "Lucas",
  companyName: "Acme Corp",
  role: "Client",
  shipmentNumber: "TG-2026-001",
  invoiceNumber: "INV-2026-042",
  amountDue: "$1,250.00",
  loginUrl: "https://app.tinyglobalop.com/login",
  supportEmail: "contact@yadata.com.au",
  note: "Please review at your earliest convenience.",
  status: "In Transit",
};

// ─── Starter templates (for New Template defaults) ────────────────────────────
const STARTERS = {
  tinyglobal: [
    { templateKey: "welcome",      name: "Welcome",              subject: "Welcome to TinyGlobal", preheader: "Your account has been created.", headerTitle: "Welcome to TinyGlobal!", bodyContent: "Hi {{recipientName}},\n\nYour TinyGlobal account has been created{{role ? ' as a ' + role : ''}}.\n\nYou can now log in and manage your shipments, customs entries, supplier collaboration, and compliance workflows — all from one place.", buttonText: "Access Your Account", buttonUrl: "{{loginUrl}}" },
    { templateKey: "shipment_update", name: "Shipment Update",   subject: "Shipment Update — TinyGlobal", preheader: "Your shipment has been updated.", headerTitle: "Shipment Update", bodyContent: "Hi {{recipientName}},\n\nYour shipment {{shipmentNumber}} has been updated.\n\nStatus: {{status}}\n\n{{note}}", buttonText: "View Shipment", buttonUrl: "{{loginUrl}}" },
    { templateKey: "customs_alert", name: "Customs Alert",       subject: "Action Required: Customs Entry", preheader: "Action required on a customs entry.", headerTitle: "Action Required", bodyContent: "Hi {{recipientName}},\n\nAction is required on one of your customs entries.\n\n{{note}}\n\nPlease log in and review at your earliest convenience to avoid delays.", buttonText: "Review Customs Entry", buttonUrl: "{{loginUrl}}" },
    { templateKey: "att_update",   name: "ATT Application Update", subject: "ATT Accreditation Update", preheader: "Update on your ATT application.", headerTitle: "ATT Accreditation Update", bodyContent: "Hi {{recipientName}},\n\nThere is an update on your ATT Accreditation application.\n\n{{note}}", buttonText: "View ATT Status", buttonUrl: "{{loginUrl}}" },
    { templateKey: "invoice_reminder", name: "Invoice Reminder", subject: "Invoice Reminder — TinyGlobal", preheader: "You have an outstanding invoice.", headerTitle: "Invoice Reminder", bodyContent: "Hi {{recipientName}},\n\nThis is a reminder that you have an outstanding invoice on your TinyGlobal account.\n\nInvoice: {{invoiceNumber}}\nAmount Due: {{amountDue}}\n\n{{note}}", buttonText: "View Invoice", buttonUrl: "{{loginUrl}}" },
    { templateKey: "announcement", name: "Platform Announcement", subject: "Message from TinyGlobal", preheader: "", headerTitle: "Platform Announcement", bodyContent: "Hi {{recipientName}},\n\n{{note}}", buttonText: "Log In to TinyGlobal", buttonUrl: "{{loginUrl}}" },
    { templateKey: "review_request", name: "Review Request",     subject: "We'd Love Your Feedback — TinyGlobal", preheader: "Share your experience.", headerTitle: "We Value Your Feedback", bodyContent: "Hi {{recipientName}},\n\nThank you for choosing TinyGlobal for your logistics and compliance needs. We truly value your partnership.\n\nIf you've had a positive experience, we'd be grateful if you could take a moment to share your feedback.\n\n{{note}}", buttonText: "Leave a Review →", buttonUrl: "{{loginUrl}}" },
    { templateKey: "onboarding",   name: "Onboarding Guide",    subject: "Getting Started with TinyGlobal", preheader: "Here's how to get started.", headerTitle: "Getting Started", bodyContent: "Hi {{recipientName}},\n\nHere's how to get the most out of TinyGlobal in your first week:\n\nStep 1 — Add your suppliers and invite them to the portal\nStep 2 — Create your first Purchase Order and share it\nStep 3 — Set up your warehouse locations for inventory tracking\nStep 4 — Run your first HS Code evaluation\nStep 5 — Book a 30-min onboarding call with our team", buttonText: "Start Exploring →", buttonUrl: "{{loginUrl}}" },
    { templateKey: "custom",       name: "Custom Message",       subject: "Message from TinyGlobal", preheader: "", headerTitle: "", bodyContent: "Hi {{recipientName}},\n\n{{note}}", buttonText: "Log In", buttonUrl: "{{loginUrl}}" },
  ],
  yadata: [
    { templateKey: "welcome",      name: "Welcome to Yadata",   subject: "Welcome to Yadata", preheader: "We're excited to work with you.", headerTitle: "Welcome to Yadata!", bodyContent: "Hi {{recipientName}},\n\nWelcome to Yadata. We're excited to be working with you{{role ? ' as a ' + role : ''}}.\n\nYadata helps Australian businesses build smarter digital infrastructure — from data platforms to supply chain software.", buttonText: "Visit Yadata →", buttonUrl: "https://www.yadata.com.au" },
    { templateKey: "services",     name: "Services Overview",   subject: "What Yadata Can Do For Your Business", preheader: "A quick look at what we offer.", headerTitle: "Our Services", bodyContent: "Hi {{recipientName}},\n\nHere's a quick overview of what Yadata can do for your business:\n\n• Logistics Operating Systems — we built TinyGlobal for Australian importers\n• Data Platforms — analytics, reporting, and integration layers\n• ERP & System Integration — connecting your existing tools\n• EDI & Supply Chain — AS2, VAN connectors, supplier collaboration\n• Custom Software — built for your operational reality\n\n{{note}}", buttonText: "Learn More →", buttonUrl: "https://www.yadata.com.au" },
    { templateKey: "project_update", name: "Project Update",    subject: "Your Yadata Project Update", preheader: "An update from your Yadata team.", headerTitle: "Project Update", bodyContent: "Hi {{recipientName}},\n\nHere's a quick update on your project with Yadata.\n\n{{note}}\n\nIf you have any questions or would like to schedule a call, please don't hesitate to reach out.", buttonText: "Reply to Your Contact →", buttonUrl: "mailto:contact@yadata.com.au" },
    { templateKey: "meeting",      name: "Meeting Request",     subject: "Let's Connect — Yadata", preheader: "We'd love to connect with you.", headerTitle: "Let's Connect", bodyContent: "Hi {{recipientName}},\n\nWe'd love to connect with you to discuss how Yadata can help your business.\n\n{{note}}\n\nPlease reply to this email or use the link below to book a time that suits you.", buttonText: "Book a Meeting →", buttonUrl: "mailto:contact@yadata.com.au" },
    { templateKey: "proposal",     name: "Proposal Follow-up",  subject: "Following Up on Our Proposal", preheader: "We wanted to follow up.", headerTitle: "Proposal Follow-up", bodyContent: "Hi {{recipientName}},\n\nFollowing up on the proposal we shared with you recently.\n\n{{note}}\n\nWe're happy to walk through any questions, adjust the scope, or arrange a call at your convenience.", buttonText: "Get in Touch →", buttonUrl: "mailto:contact@yadata.com.au" },
    { templateKey: "invoice",      name: "Invoice",             subject: "Invoice from Yadata", preheader: "Your invoice is ready.", headerTitle: "Invoice", bodyContent: "Hi {{recipientName}},\n\nPlease find your invoice from Yadata.\n\nInvoice: {{invoiceNumber}}\nAmount Due: {{amountDue}}\n\n{{note}}", buttonText: "Contact Yadata →", buttonUrl: "mailto:contact@yadata.com.au" },
    { templateKey: "newsletter",   name: "Newsletter",          subject: "From the Yadata Team", preheader: "The latest from Yadata.", headerTitle: "From the Yadata Team", bodyContent: "Hi {{recipientName}},\n\nHere's the latest from Yadata — what we've been building, thinking about, and shipping.\n\n{{note}}\n\nAs always, if there's anything we can help you with, we're just an email away.", buttonText: "Visit Yadata →", buttonUrl: "https://www.yadata.com.au" },
    { templateKey: "custom",       name: "Custom Message",      subject: "Message from Yadata", preheader: "", headerTitle: "", bodyContent: "Hi {{recipientName}},\n\n{{note}}", buttonText: "Visit Yadata →", buttonUrl: "https://www.yadata.com.au" },
  ],
};

// ─── Preview HTML builder (mirrors backend logic) ─────────────────────────────
function mergeVars(str = "", vars = {}) {
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] !== undefined ? vars[k] : `{{${k}}}`);
}

function buildPreviewHtml(editor, brand) {
  const cfg = BRANDS[brand] || BRANDS.tinyglobal;
  const primary = editor.primaryColor || cfg.primaryColor;
  const accent  = editor.accentColor  || cfg.accentColor;
  const vars    = DUMMY_VARS;

  const logoHtml = editor.logoUrl
    ? `<img src="${editor.logoUrl}" alt="${cfg.label}" style="height:40px;display:block;">`
    : brand === "tinyglobal"
      ? `<span style="color:#fff;font-size:22px;font-weight:800;">Tiny<span style="color:${accent}">Global</span></span>`
      : `<span style="color:#fff;font-size:22px;font-weight:800;">Ya<span style="color:${accent}">data</span></span>`;

  const bannerHtml = editor.bannerUrl
    ? `<img src="${editor.bannerUrl}" alt="" style="width:100%;display:block;">`
    : "";

  const bodyLines = mergeVars(editor.bodyContent || "", vars)
    .split("\n")
    .map(l => l.trim() ? `<p style="margin:0 0 14px;color:#334155;font-size:15px;line-height:1.7;">${l}</p>` : "")
    .join("");

  const btn = editor.buttonText
    ? `<div style="margin:24px 0;"><a href="#" style="display:inline-block;padding:13px 28px;background:${primary};color:#fff;text-decoration:none;border-radius:8px;font-weight:600;font-size:14px;">${mergeVars(editor.buttonText, vars)}</a></div>`
    : "";

  const footer = editor.footerText || cfg.footerText;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#f8fafc;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
<div style="max-width:600px;margin:24px auto;background:#fff;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;">
  <div style="background:${primary};padding:24px 28px;">${logoHtml}</div>
  ${bannerHtml}
  <div style="padding:28px;">
    ${editor.headerTitle ? `<h2 style="margin:0 0 18px;color:#0f172a;font-size:20px;font-weight:700;">${mergeVars(editor.headerTitle, vars)}</h2>` : ""}
    ${bodyLines}
    ${btn}
  </div>
  <div style="background:#f1f5f9;padding:18px 28px;text-align:center;font-size:12px;color:#94a3b8;white-space:pre-line;">${footer}</div>
</div></body></html>`;
}

// ─── Empty editor state ───────────────────────────────────────────────────────
function emptyEditor(brand, starter = null) {
  const cfg = BRANDS[brand] || BRANDS.tinyglobal;
  return {
    id: null,
    brand,
    templateKey: starter?.templateKey || "",
    name: starter?.name || "",
    subject: starter?.subject || "",
    preheader: starter?.preheader || "",
    headerTitle: starter?.headerTitle || "",
    bodyContent: starter?.bodyContent || "",
    buttonText: starter?.buttonText || "Log In",
    buttonUrl: starter?.buttonUrl || cfg.loginUrl,
    footerText: "",
    logoUrl: "",
    bannerUrl: "",
    primaryColor: cfg.primaryColor,
    accentColor: cfg.accentColor,
    variables: [],
    status: "active",
    _dirty: false,
  };
}

// ─── Utility ──────────────────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return "—";
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatusDot({ status }) {
  const colors = { active: "bg-emerald-500", inactive: "bg-slate-300", deleted: "bg-red-400" };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || "bg-slate-300"}`} />;
}

// ─── Image Upload helper ──────────────────────────────────────────────────────
function useImageUpload(onUrl) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef();

  const pick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setErr(null);
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const res = await api.post("/api/email/upload-image", {
            imageData: ev.target.result,
            filename: file.name,
          });
          onUrl(res.data.url);
        } catch (ex) {
          setErr(ex.response?.data?.error || "Upload failed");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
      setErr("Upload failed");
    }
  };

  return { pick, inputRef, onChange, uploading, err };
}

// ─── Variable Insert Button ───────────────────────────────────────────────────
function VarInsert({ onInsert }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
      >
        <Variable size={12} /> Insert Variable <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden">
          <p className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100">Variables</p>
          {ALL_VARS.map(v => (
            <button
              key={v.key}
              type="button"
              onClick={() => { onInsert(`{{${v.key}}}`); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-violet-50 transition-colors"
            >
              <span className="font-mono text-violet-700">{`{{${v.key}}}`}</span>
              <span className="text-slate-400 ml-2">{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Color Field ──────────────────────────────────────────────────────────────
function ColorField({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className="relative">
        <div className="w-8 h-8 rounded-lg border border-slate-200 shadow-sm overflow-hidden" style={{ background: value }}>
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
        </div>
      </div>
      <div>
        <p className="text-xs font-bold text-slate-500">{label}</p>
        <p className="text-[11px] font-mono text-slate-400">{value}</p>
      </div>
    </label>
  );
}

// ─── Editor Panel ─────────────────────────────────────────────────────────────
function EditorPanel({ editor, onUpdate, onSave, onSendTest, onDuplicate, onDelete, saving, testEmail, setTestEmail, sendingTest }) {
  const bodyRef = useRef();

  const field = (key) => ({
    value: editor[key] || "",
    onChange: e => onUpdate(key, e.target.value),
  });

  const insertVar = (v) => {
    const el = bodyRef.current;
    if (!el) { onUpdate("bodyContent", (editor.bodyContent || "") + v); return; }
    const s = el.selectionStart, e2 = el.selectionEnd;
    const next = editor.bodyContent.slice(0, s) + v + editor.bodyContent.slice(e2);
    onUpdate("bodyContent", next);
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + v.length; el.focus(); });
  };

  const logoUpload = useImageUpload(url => onUpdate("logoUrl", url));
  const bannerUpload = useImageUpload(url => onUpdate("bannerUrl", url));

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Toolbar */}
      <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between z-10 shrink-0">
        <div className="flex items-center gap-2">
          {editor._dirty && <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">Unsaved</span>}
          {editor.id && <StatusDot status={editor.status} />}
        </div>
        <div className="flex items-center gap-2">
          {editor.id && (
            <>
              <button onClick={onDuplicate} title="Duplicate" className="p-2 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"><Copy size={14} /></button>
              <button onClick={onDelete} title="Delete" className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-slate-200"><Trash2 size={14} /></button>
            </>
          )}
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-navy-900 text-white text-xs font-bold rounded-lg hover:bg-navy-800 disabled:opacity-50 transition-colors shadow-sm"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? "Saving…" : "Save Template"}
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Metadata */}
        <section>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Identity</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Template Name *</label>
              <input {...field("name")} placeholder="e.g. Welcome Email" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Template Key (slug)</label>
              <input {...field("templateKey")} placeholder="e.g. welcome" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 font-mono placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
            <select value={editor.status} onChange={e => onUpdate("status", e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none transition-all">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </section>

        {/* Envelope */}
        <section>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Envelope</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Subject Line *</label>
              <input {...field("subject")} placeholder="e.g. Welcome to TinyGlobal" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Preheader <span className="text-slate-400 font-normal">(preview text shown in inbox)</span></label>
              <input {...field("preheader")} placeholder="Short preview text…" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
          </div>
        </section>

        {/* Content */}
        <section>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Content</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Header Title</label>
              <input {...field("headerTitle")} placeholder="e.g. Welcome!" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-slate-600">Body Content</label>
                <VarInsert onInsert={insertVar} />
              </div>
              <textarea
                ref={bodyRef}
                {...field("bodyContent")}
                rows={8}
                placeholder={"Hi {{recipientName}},\n\nYour message here…"}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all resize-none font-mono leading-relaxed"
              />
              <p className="text-[10px] text-slate-400 mt-1">Each line becomes a paragraph. Use variables like <span className="font-mono">{"{{recipientName}}"}</span></p>
            </div>
          </div>
        </section>

        {/* Button */}
        <section>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Call to Action</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Button Text</label>
              <input {...field("buttonText")} placeholder="e.g. Log In →" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Button URL</label>
              <input {...field("buttonUrl")} placeholder="https://…" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all" />
            </div>
          </div>
        </section>

        {/* Branding */}
        <section>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Branding</p>
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <ColorField label="Primary Color" value={editor.primaryColor} onChange={v => onUpdate("primaryColor", v)} />
              <ColorField label="Accent Color"  value={editor.accentColor}  onChange={v => onUpdate("accentColor", v)} />
            </div>

            {/* Logo */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Logo URL</label>
              <div className="flex gap-2">
                <input {...field("logoUrl")} placeholder="https://… or upload →" className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all" />
                <input ref={logoUpload.inputRef} type="file" accept="image/*" className="hidden" onChange={logoUpload.onChange} />
                <button type="button" onClick={logoUpload.pick} disabled={logoUpload.uploading} className="px-3 py-2.5 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition-colors text-xs font-semibold flex items-center gap-1 shrink-0">
                  {logoUpload.uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
                </button>
              </div>
              {editor.logoUrl && <img src={editor.logoUrl} alt="logo preview" className="mt-2 h-8 object-contain rounded border border-slate-100 p-1 bg-slate-50" />}
            </div>

            {/* Banner */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Banner Image URL</label>
              <div className="flex gap-2">
                <input {...field("bannerUrl")} placeholder="https://… or upload →" className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all" />
                <input ref={bannerUpload.inputRef} type="file" accept="image/*" className="hidden" onChange={bannerUpload.onChange} />
                <button type="button" onClick={bannerUpload.pick} disabled={bannerUpload.uploading} className="px-3 py-2.5 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition-colors text-xs font-semibold flex items-center gap-1 shrink-0">
                  {bannerUpload.uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
                </button>
              </div>
              {editor.bannerUrl && <img src={editor.bannerUrl} alt="banner preview" className="mt-2 w-full h-16 object-cover rounded-lg border border-slate-200" />}
            </div>

            {/* Footer override */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Footer Text <span className="text-slate-400 font-normal">(leave blank for brand default)</span></label>
              <textarea {...field("footerText")} rows={2} placeholder="Leave blank to use brand default…" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all resize-none" />
            </div>
          </div>
        </section>

        {/* Send Test */}
        <section className="border-t border-slate-100 pt-5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Send Test Email</p>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
            />
            <button
              onClick={onSendTest}
              disabled={sendingTest || !testEmail || !editor.id}
              className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white text-xs font-bold rounded-xl hover:bg-violet-700 disabled:opacity-40 transition-colors shrink-0"
            >
              {sendingTest ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
              {sendingTest ? "Sending…" : "Send Test"}
            </button>
          </div>
          {!editor.id && <p className="text-[11px] text-slate-400 mt-1.5">Save the template first to enable test sending.</p>}
        </section>
      </div>
    </div>
  );
}

// ─── Preview Panel ────────────────────────────────────────────────────────────
function PreviewPanel({ editor, brand }) {
  const [mode, setMode] = useState("desktop");
  const previewHtml = useMemo(() => buildPreviewHtml(editor, brand), [editor, brand]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live Preview</p>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button onClick={() => setMode("desktop")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ${mode === "desktop" ? "bg-navy-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Monitor size={12} /> Desktop
          </button>
          <button onClick={() => setMode("mobile")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ${mode === "mobile" ? "bg-navy-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Smartphone size={12} /> Mobile
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-slate-100 p-4 flex justify-center">
        <div className={`bg-white rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${mode === "mobile" ? "w-[375px]" : "w-full max-w-[620px]"}`}>
          <iframe
            title="email-preview"
            srcDoc={previewHtml}
            className="w-full border-0"
            style={{ height: "600px" }}
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Compose Tab ──────────────────────────────────────────────────────────────
function ComposeTab({ templates }) {
  const [selectedId, setSelectedId] = useState("");
  const [to, setTo] = useState("");
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);

  const template = templates.find(t => t.id === selectedId) || null;

  const detectedVars = useMemo(() => {
    if (!template) return [];
    const re = /\{\{(\w+)\}\}/g;
    const found = new Set();
    for (const f of ["subject", "headerTitle", "bodyContent", "buttonText"]) {
      let m;
      const str = template[f] || "";
      while ((m = re.exec(str)) !== null) found.add(m[1]);
      re.lastIndex = 0;
    }
    return [...found];
  }, [template]);

  useEffect(() => { setVars({}); }, [selectedId]);

  const previewHtml = useMemo(() => {
    if (!template) return "";
    return buildPreviewHtml({ ...template, ...template }, template.brand || "tinyglobal");
  }, [template]);

  const handleSend = async () => {
    if (!to || !selectedId) return;
    setSending(true);
    setNotice(null);
    try {
      await api.post("/api/email/send", { templateId: selectedId, to: to.trim(), vars });
      setNotice({ ok: true, msg: `Email sent to ${to}` });
      setTo("");
      setVars({});
    } catch (err) {
      setNotice({ ok: false, msg: err.response?.data?.error || "Send failed." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-5 space-y-4">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Select Template</label>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all">
            <option value="">— Choose a template —</option>
            {templates.filter(t => t.status === "active").map(t => (
              <option key={t.id} value={t.id}>[{BRANDS[t.brand]?.label || t.brand}] {t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Recipient Email</label>
          <input type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="client@company.com" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all" />
        </div>
        {detectedVars.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Variables</p>
            {detectedVars.map(v => {
              const meta = ALL_VARS.find(a => a.key === v);
              return (
                <div key={v}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{meta?.label || v}</label>
                  <input
                    value={vars[v] || ""}
                    onChange={e => setVars(p => ({ ...p, [v]: e.target.value }))}
                    placeholder={`Enter ${meta?.label || v}…`}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                  />
                </div>
              );
            })}
          </div>
        )}
        {notice && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border ${notice.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            {notice.ok ? <CheckCircle size={15} /> : <AlertCircle size={15} />} {notice.msg}
          </div>
        )}
        <button onClick={handleSend} disabled={sending || !to || !selectedId} className="w-full py-3 bg-navy-900 text-white font-bold rounded-xl text-sm hover:bg-navy-800 disabled:opacity-40 flex items-center justify-center gap-2 transition-colors">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {sending ? "Sending…" : "Send Email"}
        </button>
      </div>

      <div className="col-span-12 md:col-span-7 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Preview</p>
        </div>
        {template
          ? <iframe title="compose-preview" srcDoc={previewHtml} className="w-full border-0" style={{ height: "500px" }} sandbox="allow-same-origin" />
          : <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Select a template to preview</div>
        }
      </div>
    </div>
  );
}

// ─── Log Tab ──────────────────────────────────────────────────────────────────
function LogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/email-center/logs");
      setLogs(res.data.logs || []);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return logs;
    const s = search.toLowerCase();
    return logs.filter(l => [l.to, l.template, l.templateId, l.subject, l.brand].some(v => (v || "").toLowerCase().includes(s)));
  }, [logs, search]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by recipient, template, or subject…" className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all" />
        </div>
        <button onClick={fetch} className="p-2.5 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      {loading
        ? <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
        : filtered.length === 0
          ? <div className="flex flex-col items-center py-20 text-center"><Clock size={32} className="text-slate-200 mb-3" /><p className="font-bold text-slate-400">No emails sent yet</p></div>
          : (
            <table className="w-full text-left">
              <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/60 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4">Recipient</th>
                  <th className="px-6 py-4">Brand</th>
                  <th className="px-6 py-4">Subject</th>
                  <th className="px-6 py-4">Sent</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-navy-900">{log.to}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${log.brand === "yadata" ? "bg-blue-100 text-blue-700" : "bg-navy-100 text-navy-700"}`}>{BRANDS[log.brand]?.label || log.brand || "TinyGlobal"}</span></td>
                    <td className="px-6 py-4 text-sm text-slate-500 max-w-xs truncate">{log.subject}</td>
                    <td className="px-6 py-4 text-xs text-slate-400 font-medium whitespace-nowrap">{timeAgo(log.sentAt)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${log.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{log.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
      }
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EmailCenter() {
  const [activeTab, setActiveTab] = useState("templates");
  const [brand, setBrand] = useState("tinyglobal");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [showStarterModal, setShowStarterModal] = useState(false);

  const fetchTemplates = useCallback(async (b = brand) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/email/templates?brand=${b}`);
      setTemplates(res.data.templates || []);
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  }, [brand]);

  useEffect(() => { fetchTemplates(brand); }, [brand]);

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const s = search.toLowerCase();
    return templates.filter(t => (t.name || "").toLowerCase().includes(s) || (t.templateKey || "").toLowerCase().includes(s));
  }, [templates, search]);

  const flash = (msg, ok = true) => {
    setNotice({ msg, ok });
    setTimeout(() => setNotice(null), 3500);
  };

  const handleSelectTemplate = (t) => {
    setEditor({ ...t, _dirty: false });
  };

  const handleNewFromStarter = (starter) => {
    setEditor(emptyEditor(brand, starter));
    setShowStarterModal(false);
  };

  const handleUpdate = (key, value) => {
    setEditor(prev => ({ ...prev, [key]: value, _dirty: true }));
  };

  const handleSave = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      if (editor.id) {
        await api.put(`/api/email/templates/${editor.id}`, editor);
        flash("Template saved.");
        setEditor(prev => ({ ...prev, _dirty: false }));
      } else {
        const res = await api.post("/api/email/templates", editor);
        flash("Template created.");
        setEditor(prev => ({ ...prev, id: res.data.id, _dirty: false }));
      }
      fetchTemplates(brand);
    } catch (err) {
      flash(err.response?.data?.error || "Save failed.", false);
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!editor?.id) return;
    try {
      const res = await api.post(`/api/email/templates/${editor.id}/duplicate`);
      flash("Template duplicated.");
      fetchTemplates(brand);
    } catch {
      flash("Duplicate failed.", false);
    }
  };

  const handleDelete = async () => {
    if (!editor?.id || !window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/api/email/templates/${editor.id}`);
      flash("Template deleted.");
      setEditor(null);
      fetchTemplates(brand);
    } catch {
      flash("Delete failed.", false);
    }
  };

  const handleSendTest = async () => {
    if (!editor?.id || !testEmail) return;
    setSendingTest(true);
    try {
      await api.post(`/api/email/templates/${editor.id}/send-test`, { to: testEmail, vars: DUMMY_VARS });
      flash(`Test email sent to ${testEmail}`);
    } catch (err) {
      flash(err.response?.data?.error || "Test send failed.", false);
    } finally {
      setSendingTest(false);
    }
  };

  const allTemplates = [...templates];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2.5">
            <Mail size={24} className="text-navy-900" /> Email Center
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">Build, preview, and send branded transactional emails.</p>
        </div>
        <div className="flex items-center gap-2">
          {notice && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border ${notice.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {notice.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {notice.msg}
            </div>
          )}
          {["templates", "compose", "log"].map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-4 py-2 rounded-xl font-bold text-sm capitalize transition-all ${activeTab === t ? "bg-navy-900 text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              {t === "log" ? "Sent Log" : t === "compose" ? "Compose" : "Templates"}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden p-6">
        {/* ── Templates Tab ── */}
        {activeTab === "templates" && (
          <div className="grid grid-cols-12 gap-4 h-full">
            {/* Left: template list */}
            <div className="col-span-12 md:col-span-3 flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Brand filter */}
              <div className="p-3 border-b border-slate-100">
                <div className="flex rounded-xl overflow-hidden border border-slate-200">
                  {Object.entries(BRANDS).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => { setBrand(key); setEditor(null); }}
                      className={`flex-1 py-2 text-xs font-bold transition-colors ${brand === key ? "bg-navy-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}
                    >
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search */}
              <div className="px-3 py-2 border-b border-slate-100">
                <div className="relative">
                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates…" className="w-full pl-8 pr-3 py-2 text-xs text-slate-900 placeholder-slate-400 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-navy-900/10" />
                </div>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
                {loading
                  ? <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-300" /></div>
                  : filteredTemplates.length === 0
                    ? <div className="py-8 text-center text-slate-400 text-xs">No templates yet</div>
                    : filteredTemplates.map(t => (
                      <button
                        key={t.id}
                        onClick={() => handleSelectTemplate(t)}
                        className={`w-full text-left px-4 py-3.5 flex items-center justify-between gap-2 transition-colors hover:bg-slate-50 ${editor?.id === t.id ? "bg-navy-50 border-l-2 border-navy-900" : ""}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <StatusDot status={t.status} />
                            <p className={`font-bold text-sm truncate ${editor?.id === t.id ? "text-navy-900" : "text-slate-700"}`}>{t.name}</p>
                          </div>
                          <p className="text-[11px] text-slate-400 truncate font-mono">{t.templateKey}</p>
                        </div>
                        <ChevronRight size={13} className="text-slate-300 shrink-0" />
                      </button>
                    ))
                }
              </div>

              {/* New template */}
              <div className="p-3 border-t border-slate-100">
                <button
                  onClick={() => setShowStarterModal(true)}
                  className="w-full py-2.5 border-2 border-dashed border-slate-200 text-slate-500 text-xs font-bold rounded-xl hover:border-navy-300 hover:text-navy-700 hover:bg-navy-50 transition-all flex items-center justify-center gap-2"
                >
                  <Plus size={14} /> New Template
                </button>
              </div>
            </div>

            {/* Middle: editor */}
            <div className="col-span-12 md:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              {editor ? (
                <EditorPanel
                  editor={editor}
                  onUpdate={handleUpdate}
                  onSave={handleSave}
                  onSendTest={handleSendTest}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  saving={saving}
                  testEmail={testEmail}
                  setTestEmail={setTestEmail}
                  sendingTest={sendingTest}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400">
                  <Mail size={40} className="mb-4 text-slate-200" />
                  <p className="font-bold text-slate-500 mb-1">No Template Selected</p>
                  <p className="text-sm">Pick a template from the list, or create a new one.</p>
                </div>
              )}
            </div>

            {/* Right: preview */}
            <div className="col-span-12 md:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              {editor ? (
                <PreviewPanel editor={editor} brand={brand} />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400">
                  <Eye size={40} className="mb-4 text-slate-200" />
                  <p className="font-bold text-slate-500 mb-1">Live Preview</p>
                  <p className="text-sm">Select or create a template to see a live preview here.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Compose Tab ── */}
        {activeTab === "compose" && <ComposeTab templates={allTemplates} />}

        {/* ── Log Tab ── */}
        {activeTab === "log" && <LogTab />}
      </div>

      {/* Starter Template Modal */}
      {showStarterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-navy-900">New Template</h3>
                <p className="text-xs text-slate-400 mt-0.5">Start from a starter or blank</p>
              </div>
              <button onClick={() => setShowStarterModal(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"><X size={16} /></button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              <button
                onClick={() => handleNewFromStarter(null)}
                className="w-full text-left px-4 py-3 mb-2 border-2 border-dashed border-slate-200 rounded-xl hover:border-navy-300 hover:bg-navy-50 transition-all group"
              >
                <p className="font-bold text-sm text-slate-700 group-hover:text-navy-900">Blank Template</p>
                <p className="text-xs text-slate-400">Start from scratch</p>
              </button>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 mt-4 px-1">{BRANDS[brand]?.label} Starters</p>
              <div className="space-y-1.5">
                {(STARTERS[brand] || []).map(s => (
                  <button
                    key={s.templateKey}
                    onClick={() => handleNewFromStarter(s)}
                    className="w-full text-left px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl hover:border-navy-300 hover:bg-navy-50 transition-all group"
                  >
                    <p className="font-bold text-sm text-slate-700 group-hover:text-navy-900">{s.name}</p>
                    <p className="text-xs text-slate-400 truncate">{s.subject}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
